'use client';

import { useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';

import { SmartLink as Link } from './smart-link';
import type { ServicesBlock } from '~/(tenant)/_lib/schema/landing-config.schema';

import { type LandingRenderContext, pickLocalized } from './landing-context';
import styles from './services-tabs-accordion.module.css';
import { LandingProse } from './rich-text';

export function ServicesTabsAccordion({ block, ctx }: { block: ServicesBlock; ctx: LandingRenderContext }) {
  const id = useId();
  const categories = block.categories ?? [];
  const [selection, setSelection] = useState({ initial: block.initialCategoryId, selected: block.initialCategoryId ?? categories[0]?.id });
  const selected = selection.initial === block.initialCategoryId ? selection.selected : block.initialCategoryId;
  if (selection.initial !== block.initialCategoryId) {
    setSelection({ initial: block.initialCategoryId, selected: block.initialCategoryId });
  }
  const setSelected = (selected: string) => setSelection({ initial: block.initialCategoryId, selected });
  const active = block.mode === 'manual' ? categories.find((category) => category.id === selected) ?? categories[0] : undefined;
  const local = (en?: string, ar?: string) => pickLocalized(en, ar, ctx.locale);
  const arabic = ctx.locale.startsWith('ar');
  const cards: NonNullable<ServicesBlock['cards']> = block.mode === 'auto' ? ctx.services.map((service) => ({ title: service.name, description: service.description, href: block.autoLinkToBooking === false ? undefined : '/book' })) : block.cards ?? [];
  const visibleCards = active && block.mode === 'manual' ? cards.filter((card) => card.categoryIds?.includes(active.id)) : cards;

  return (
    <section className={styles.section} dir={arabic ? 'rtl' : undefined} data-test="services-tabs-accordion">
      {block.eyebrow ? <p className="landing-eyebrow">{local(block.eyebrow, block.eyebrow_ar)}</p> : null}
      {block.title ? <h2>{local(block.title, block.title_ar)}</h2> : null}
      {block.subtitle ? <p className="landing-prose">{local(block.subtitle, block.subtitle_ar)}</p> : null}
      {categories.length && block.mode === 'manual' ? (
        <div className={styles.tabs} role="tablist" aria-label={local(block.title, block.title_ar) ?? (arabic ? 'الأنشطة' : 'Activities')}>
          {categories.map((category, index) => (
            <button key={category.id} type="button" role="tab" id={`${id}-tab-${category.id}`} aria-controls={`${id}-panel`} aria-selected={active?.id === category.id} tabIndex={active?.id === category.id ? 0 : -1} onClick={() => setSelected(category.id)} onKeyDown={(event) => {
              const direction = arabic ? -1 : 1;
              const next = event.key === 'Home' ? 0 : event.key === 'End' ? categories.length - 1 : event.key === 'ArrowRight' ? (index + direction + categories.length) % categories.length : event.key === 'ArrowLeft' ? (index - direction + categories.length) % categories.length : null;
              if (next === null) return;
              event.preventDefault();
              const category = categories[next]!;
              setSelected(category.id);
              event.currentTarget.ownerDocument.getElementById(`${id}-tab-${category.id}`)?.focus();
            }}>{local(category.label, category.label_ar)}</button>
          ))}
        </div>
      ) : null}
      <div id={`${id}-panel`} role={active ? 'tabpanel' : undefined} aria-labelledby={active ? `${id}-tab-${active.id}` : undefined} tabIndex={active ? 0 : undefined} className={styles.panel} key={active?.id}>
        {active ? <><h3>{local(active.label, active.label_ar)}</h3><LandingProse ctx={{ ...ctx, richText: true }} text={local(active.description, active.description_ar) ?? ''} className={styles.intro} /></> : null}
        <div className={styles.items}>
          {visibleCards.map((card, index) => (
            <details key={index} className={styles.item}>
              <summary>{local(card.title, card.title_ar)}<ChevronDown size={16} aria-hidden="true" /></summary>
              <div className={styles.body}>
                <p className="landing-prose">{local(card.description, card.description_ar)}</p>
                {card.intensity ? <p className={styles.intensity}><strong>{arabic ? 'الشدة:' : 'Intensity:'}</strong><span role="img" aria-label={arabic ? `${card.intensity} من 5` : `${card.intensity} out of 5`}>{Array.from({ length: 5 }, (_, i) => <i key={i} data-filled={i < card.intensity! || undefined} />)}</span></p> : null}
                {card.bullets?.length ? <ul>{card.bullets.map((bullet, i) => <li key={i}>{local(bullet, card.bullets_ar?.[i])}</li>)}</ul> : null}
                {card.href && !card.ctaDisabled ? <Link href={card.href} className="landing-button">{local(card.ctaLabel, card.ctaLabel_ar) ?? (arabic ? 'احجزي الآن' : 'Book now')}</Link> : null}
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}
